import { Box, Button, Card, Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { PageSize, QueueReviewStatus, ReviewQueueRow } from 'api';
import { UnavailableIndicator } from './UnavailableIndicator';

type StatusColor = React.ComponentProps<typeof Chip>['color'];
const STATUS_COLOR: Record<QueueReviewStatus, StatusColor> = {
  NeedsReview: 'warning',
  Reviewed: 'success',
};
const STATUS_LABEL: Record<QueueReviewStatus, string> = {
  NeedsReview: 'Needs review',
  Reviewed: 'Reviewed',
};

// documentDate has no zone designator, so dayjs reads it as local time, as intended.
const formatDate = (value: string | null) =>
  value ? dayjs(value).format('MMM D, YYYY') : null;

const formatConfidence = (value: number | null) =>
  value == null ? null : `${Math.round(value * 100)}%`;

type QueueListProps = {
  rows: ReviewQueueRow[];
  totalCount: number;
  loading: boolean;
  page: number; // 1-based
  pageSize: PageSize;
  onPaginationChange: (page: number) => void;
  selectedJobKey: number | null;
  onSelectJob: (jobKey: number | null) => void;
};

export const QueueList = ({
  rows,
  totalCount,
  loading,
  page,
  pageSize,
  onPaginationChange,
  selectedJobKey,
  onSelectJob,
}: QueueListProps) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (totalCount === 0 && !loading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary" align="center" sx={{ px: 2 }}>
          No documents match the active filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
        {rows.map(row => {
          const active = row.extractionJobKey === selectedJobKey;
          return (
            <Card
              key={row.extractionJobKey}
              variant="outlined"
              onClick={() => onSelectJob(row.extractionJobKey)}
              sx={{
                p: 1.5,
                mb: 1,
                cursor: 'pointer',
                borderColor: active ? 'primary.main' : undefined,
                borderWidth: active ? 2 : 1,
                backgroundColor: active ? 'action.selected' : undefined,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{ minWidth: 0 }}
                  title={String(row.srcDocumentKey)}
                >
                  Doc {row.srcDocumentKey}
                </Typography>
                <Chip
                  label={STATUS_LABEL[row.reviewStatus]}
                  color={STATUS_COLOR[row.reviewStatus]}
                  size="small"
                  variant="outlined"
                  sx={{ flexShrink: 0 }}
                />
              </Box>

              <Typography variant="body2" noWrap>
                {row.typeText ?? (
                  <UnavailableIndicator label="Document type unavailable" inline />
                )}
                {row.extractionDescription ? ` · ${row.extractionDescription}` : null}
              </Typography>

              <Typography variant="caption" color="text.secondary" display="block">
                MRN{' '}
                {row.mrn == null ? (
                  <UnavailableIndicator label="Unresolved" inline />
                ) : (
                  row.mrn
                )}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {row.ooDataPartnerKey ?? 'Unavailable'} ·{' '}
                  {formatDate(row.documentDate) ?? 'Unavailable'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatConfidence(row.ocrConfidence) ?? 'Unavailable'} OCR
                </Typography>
              </Box>
            </Card>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 1,
        }}
      >
        <Button
          size="small"
          disabled={page <= 1 || loading}
          onClick={() => onPaginationChange(page - 1)}
        >
          Previous
        </Button>
        <Typography variant="caption" color="text.secondary">
          Page {page} of {totalPages}
        </Typography>
        <Button
          size="small"
          disabled={page >= totalPages || loading}
          onClick={() => onPaginationChange(page + 1)}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
